import type Coupon from "@interfaces/Coupon";
import type { Promo, PromoListResponse } from "@interfaces/Promo";
import axios from "@utilities/axios";
import { getSiteUrl } from "@utilities/base-url";
import { getPaymentPublishableKey, isDebugMode } from "@utilities/helper";

export const fetchPromos = async (language: string): Promise<Promo[]> => {
  const appId = process.env.GATSBY_APP_ID ?? "";
  if (appId === "") return [];
  const formData = new FormData();
  formData.append("app_id", appId);
  formData.append("language", language);
  // Identify this caller as the website so the backend can return promos
  // targeted at the web channel (platform BOTH or WEB_ONLY) and hide APP_ONLY
  // ones. The mobile app omits this / sends "app".
  formData.append("platform", "web");
  const response = await axios.post<PromoListResponse>("/promo/", formData);
  return response.data?.promos ?? [];
};

interface PaymentMethodsResponse {
  coupons?: Coupon[];
}

// Fetches the authenticated user's coupons scoped to the current app + locale.
// Caller must verify auth before calling — the axios wrapper attaches the
// bearer token automatically.
export const fetchMyCoupons = async (
  currency: string,
  language: string,
): Promise<Coupon[]> => {
  const formData = new FormData();
  formData.append("publishable_key", getPaymentPublishableKey());
  formData.append("currency", currency);
  formData.append("appid", process.env.GATSBY_APP_ID ?? "");
  formData.append("language", language);
  const response = await axios.post<PaymentMethodsResponse>(
    "/payment_methods/",
    formData,
  );
  if (isDebugMode()) {
    console.log("[promo] my coupons response:", response.data);
  }
  return response.data?.coupons ?? [];
};

// Sort promos by numeric id, descending (newest id first).
export const sortPromos = (promos: Promo[]): Promo[] =>
  [...promos].sort((a, b) => {
    const rawA = parseInt(a.id, 10);
    const rawB = parseInt(b.id, 10);
    const idA = Number.isFinite(rawA) ? rawA : 0;
    const idB = Number.isFinite(rawB) ? rawB : 0;
    return idB - idA;
  });

export const isFeatured = (p: Promo): boolean =>
  p.app_promo_type === "FEATURED";

export const isPromotion = (p: Promo): boolean =>
  p.app_promo_type === "PROMOTION";

export interface ParsedPromoLink {
  href: string;
  isInternal: boolean;
}

// Reads the promo's `web_link` field. Returns null when there's nothing to
// link to so the caller can hide the CTA. A URL that begins with the site's
// own origin (`getSiteUrl()`) is treated as internal — the matching prefix
// is stripped so `<Link>` receives a relative path. Anything else is
// treated as external.
export const parsePromoLink = (promo: Promo): ParsedPromoLink | null => {
  const link = (promo.web_link ?? "").trim();
  if (link === "") return null;

  const siteUrl = getSiteUrl();
  if (siteUrl !== "" && link.startsWith(siteUrl)) {
    const path = link.slice(siteUrl.length);
    return {
      href: path === "" ? "/" : path,
      isInternal: true,
    };
  }
  return {
    href: link,
    isInternal: false,
  };
};
